import axios from '@/plugins/axios'
import type { ApiResponse, PlanLine, Session, Student } from '@/types/global'
import { calculateTotals } from '@/utils/calculateTotalLinesAndPages'
import { context } from '.'

export const fetchSingleSessionStudents = async (session: Session) => {
  const response = await axios.post<ApiResponse<Student[]>>(`/api/${context}/api/1.0/student`, {
    jsonrpc: '2.0',
    params: {
      filter: [
        ['id', 'in', session.student_ids.map((s) => s.id)],
        ['state', '=', 'done']
      ]
    }
  })

  return response.data.result
}

export const fetchSessionsStudents = async (sessions: Session[], fields?: string[]) => {
  const result: { [key: number]: Student[] } = {}

  const all = await fetchAllStudents(
    [
      [
        'id',
        'in',
        sessions.reduce((acc: number[], s) => [...acc, ...s.student_ids.map((s) => s.id)], [])
      ],
      ['state', '=', 'done']
    ],
    fields
  )

  for (const session of sessions) {
    result[session.id] = all.filter((s) => session.student_ids.map((s) => s.id).includes(s.id))
  }

  return result
}

export const fetchAllStudents = async (
  filters: [string, string, any][] = [],
  fields?: string[]
) => {
  const response = await axios.post<ApiResponse<Student[]>>(`/api/${context}/api/1.0/student`, {
    jsonrpc: '2.0',
    params: {
      fields,
      filter: [...filters, ['state', '=', 'done']]
    }
  })

  if (response.data.error) {
    throw new Error(response.data?.error.message)
  }

  return response.data.result
}

export const fetchStudent = async (id: number) => {
  const response = await axios.post<ApiResponse<Student>>(
    `/api/${context}/api/1.0/student/${id}`,
    {
      jsonrpc: '2.0',
      params: {
        filter: [['state', '=', 'done']]
      }
    }
  )

  return response.data.result
}

export type StudentForm = {
  id?: number
  first_name: string
  middle_name: string
  grandfather_name: string
  last_name: string
  mobile: string
  email: string
  civil_registry: string
  nationality_id: number
  date_of_birth: string
  address: string
  education_type: string
  educational_stage: string
  ensure_days: any
  company_branch_id: number
  mosque_id: number
  student_parent: string
  student_parent_mobile: string

  // tab 2 data
  student_session_id: number

  date_start_plan: string
  date_end_plan: string

  daily_recitation_plan_id: number
  recitation_start_id: number

  daily_revision_plan_id: number
  revision_start_id: number
}

export const createStudent = async (student: Partial<StudentForm>) => {
  const response = await axios.post<ApiResponse<Student | { status: boolean; message: string }>>(
    `/api/${context}/api/1.0/student/create`,
    {
      jsonrpc: '2.0',
      params: Object.assign(student, {
        date_end_plan:
          !student.date_end_plan || student.date_end_plan === '' ? false : student.date_end_plan,
        date_start_plan:
          !student.date_start_plan || student.date_start_plan === ''
            ? false
            : student.date_start_plan
      })
    }
  )

  if (!(response.data.result as { status: boolean; message: string }).status) {
    return Promise.reject((response.data.result as { status: boolean; message: string }).message)
  }
  return response.data.result as Student
}

export const getStudentsStatistics = (
  students: { [key: number]: Student[] },
  memorizePlans: PlanLine[][] = [],
  reviewPlans: PlanLine[][] = []
) => {
  if (!students || !Object.keys(students).length)
    return {
      memorizeSum: 0,
      totalStudents: 0,
      ensureSum: 0,
      allStudents: 0,
      reviewSum: 0,
      totalEvaluations: 0,
      totalMemorize: 0,
      totalReview: 0
    }

  return (Object.keys(students as Object) as string[]).reduce(
    (acc, key) => {
      acc.allStudents += students[Number.parseInt(key)].filter((s) =>
        s.evaluations?.some((e) => e.presence_type == 'present' || e.presence_type == 'late')
      ).length

      for (const student of students[Number.parseInt(key)]) {
        acc.totalStudents++

        if (
          !student.evaluations?.some(
            (e) => e.presence_type == 'present' || e.presence_type == 'late'
          ) ||
          !student.current_plan_id
        ) {
          continue
        }

        acc.memorizeSum += student.evaluations?.reduce((acc, e) => {
          // if (!e.degree_memorize) return acc

          console.log(e.total_memorize_page)

          return acc + Number.parseFloat(e.total_memorize_page as string)
        }, 0)

        acc.ensureSum += student.evaluations?.reduce((acc, e) => {
          if (!e.degree_ensure) return acc

          return acc + Number.parseFloat((e.degree_ensure as string) ?? 0)
        }, 0)
        acc.reviewSum += student.evaluations?.reduce((acc, e) => {
          // if (!e.degree_review) return acc

          // console.log(e.total_review_page);

          return acc + Number.parseFloat(e.total_review_page as string)
        }, 0)
      }

      return acc
    },
    {
      memorizeSum: 0,
      allStudents: 0,

      totalStudents: 0,
      ensureSum: 0,
      reviewSum: 0,
      totalMemorize: memorizePlans.reduce((acc, p) => {
        return (
          acc +
          p.reduce((acc, l) => {
            return (
              acc +
              calculateTotals(l.surah_from_id.id, l.verse_from, l.surah_to_id.id, l.verse_to)
                .totalPages
            )
          }, 0)
        )
      }, 0),

      totalReview: reviewPlans.reduce((acc, p) => {
        return (
          acc +
          p.reduce((acc, l) => {
            return (
              acc +
              calculateTotals(l.surah_from_id.id, l.verse_from, l.surah_to_id.id, l.verse_to)
                .totalPages
            )
          }, 0)
        )
      }, 0)
    }
  )
}

export const viewStudent = async (id: string) => {
  const response = await axios.post<ApiResponse<Student>>(
    `/api/${context}/api/1.0/student/view/${id}`,
    {
      jsonrpc: '2.0',
      params: {
        check_token: parseInt(id) * 272333
      }
    }
  )

  return response.data.result
}

export const moveStudents = async (student_ids: number[], session_id: number) => {
  try {
    const response = await axios.post<ApiResponse<Student>>(
      `/api/${context}/api/1.0/session/student/update`,
      {
        jsonrpc: '2.0',
        params: {
          session_id,
          student_ids
        }
      }
    )

    return response.data.result
  } catch (error) {
    return Promise.reject(new Error('something went wrong'))
  }
}

export const sendNextPlan = async (studentId: number) => {
  const response = await axios.post<ApiResponse<Student[]>>(
    `/api/${context}/api/1.0/send_ayat`,
    {
      jsonrpc: '2.0',
      params: {
        student_id: studentId
      }
    }
  )

  if (response.data.result.error) {
    return Promise.reject(response.data.result.error)
  }

  return response.data.result
}
